import { useState, useEffect, useCallback } from "react";
import { Modal, message } from "antd";
import { useTranslation } from "react-i18next";

import {
  getGuardians,
  deleteGuardian as deleteGuardianApi,
  updateGuardian as updateGuardianApi,
  searchGuardians,
  createGuardian,
} from "../../../../services/guardianService";

import {
  Guardian,
  GuardianRelationship,
  GuardianSearchParams,
  GuardianUpdatePayload,
} from "../../../../types/guardianTypes";
import { sendResetPassword } from "services/userService";

export const useGuardians = () => {
  const { t } = useTranslation();

  const [guardians, setGuardians] = useState<Guardian[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<any>({
    page: 1,
    limit: 10,
    total: 0,
  });
  // UI state
  const [selectedGuardian, setSelectedGuardian] = useState<Guardian | null>(null);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);

  // Search modal state
  const [matchedGuardian, setMatchedGuardian] = useState<Guardian | null>(null);
  const [notFound, setNotFound] = useState<boolean>(false);

  // Fetch all guardians
  const fetchGuardians = useCallback(async (filters: any = { page: 1, limit: 10 }) => {
    setLoading(true);
    try {
      const response = await getGuardians(filters);
      setGuardians(response.data);
      setPagination(response.pagination)
      setError(null);
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_fetch_guardians"));
      setError(t("failed_to_fetch_guardians"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Delete guardian
  const deleteGuardian = useCallback(
    async (id: string) => {
      try {
        await deleteGuardianApi(id);
        message.success(t("guardian_deleted_successfully"));
        await fetchGuardians();
      } catch (err) {
        console.error(err);
        message.error(t("failed_to_delete_guardian"));
      }
    },
    [fetchGuardians, t]
  );

  const handleDelete = (guardianId: string) => {
    Modal.confirm({
      title: t("confirm_delete"),
      content: t("delete_guardian_warning"),
      okText: t("delete"),
      okType: "danger",
      cancelText: t("cancel"),
      centered: true,
      onOk: () => deleteGuardian(guardianId),
    });
  };

  const handleUpdate = async (guardianId: string, updatedData: GuardianUpdatePayload) => {
    try {
      await updateGuardianApi(guardianId, updatedData);
      message.success(t("guardian_updated_successfully"));
      setEditModalVisible(false);
      fetchGuardians();
    } catch (error) {
      console.error(error);
      message.error(t("failed_to_update_guardian"));
    }
  };

  // Search for guardian by email/name
  const search = async (params: GuardianSearchParams) => {
    setLoading(true);
    setNotFound(false);
    try {
      const response = await searchGuardians(params);
      const results = response.data;

      if (results.length > 0) {
        setMatchedGuardian(results[0]);
      } else {
        setMatchedGuardian(null);
        setNotFound(true);
      }
    } catch (err) {
      console.error("Guardian search error:", err);
    } finally {
      setLoading(false);
    }
  };

  const resetSearch = () => {
    setMatchedGuardian(null);
    setNotFound(false);
  };

  // Link students to existing guardian
  const linkStudents = async (
    guardianId: string,
    studentIds: string[],
    relationship: GuardianRelationship,
  ) => {
    if (!guardianId || studentIds.length === 0) return;
    setLoading(true);
    try {
      await updateGuardianApi(guardianId, { studentIds, relationship });
    } catch (err) {
      console.error("Failed to link students", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createAndLinkGuardian = async (
    user: {
      email: string;
      firstName: string;
      lastName: string;
      phoneNumber?: string;
    },
    studentIds: string | string[], // 🔄 Accept single or multiple
    relationship: GuardianRelationship
  ) => {
    setLoading(true);
    try {
      const normalizedStudentIds = Array.isArray(studentIds) ? studentIds : [studentIds];

      await createGuardian({
        user,
        studentIds: normalizedStudentIds,
        relationship,
      });
    } catch (err) {
      console.error("Failed to create and link guardian", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };


  const triggerResetPassword = async (body: { user_id?: string; type: "email" | "sms" }) => {
    try {
      const response = await sendResetPassword(body);
      const messageSuccess = (body.type == "email" ? t("reset_password_via_email_success") : t("reset_password_via_sms_success"));
      message.success(messageSuccess);
      return response
    } catch (err) {
      return false
    }
  };


  useEffect(() => {
    fetchGuardians();
  }, [fetchGuardians]);

  return {
    // Data
    guardians,
    loading,
    error,
    pagination,

    // Modals & UI
    selectedGuardian,
    setSelectedGuardian,
    editModalVisible,
    setEditModalVisible,
    showDetailsModal,
    setShowDetailsModal,
    showAddModal,
    setShowAddModal,

    // Actions
    fetchGuardians,
    handleDelete,
    handleUpdate,
    createAndLinkGuardian,

    // Search + link
    matchedGuardian,
    notFound,
    search,
    resetSearch,
    linkStudents,
    triggerResetPassword,
  };
};
